// One avatar, three variants, three sizes (phase 23 Task 1).
//
// Circular. The variant is load-bearing rather than decorative: an outlined
// avatar says "this record has no history", a breach-outlined one says "this
// record is inert".

type Variant = 'filled' | 'outlined' | 'blocked';
type Size = 28 | 46 | 52;

/** Initials from a name, falling back to the email's local part. */
export function initialsFor(name: string | null | undefined, email: string): string {
  const from = (name ?? '').trim() || (email.split('@')[0] ?? '');
  const words = from.split(/[\s._-]+/).filter(Boolean);
  const letters = words.length > 1 ? [words[0][0], words[1][0]] : [from[0], from[1]];
  return letters.filter(Boolean).join('').toUpperCase() || '?';
}

const FONT_SIZE: Record<Size, number> = { 28: 10.5, 46: 15, 52: 17 };

export function CustomerAvatar({
  initials,
  size = 28,
  variant = 'filled',
}: {
  initials: string;
  size?: Size;
  variant?: Variant;
}) {
  const style =
    variant === 'filled'
      ? { background: 'var(--co-avatar)', color: 'var(--co-ink-invert)' }
      : variant === 'blocked'
        ? {
            // Outline only, no fill: the record is inert, and a solid avatar
            // reads as an active person.
            boxShadow: 'inset 0 0 0 1px var(--co-burgundy-line)',
            color: 'var(--co-burgundy)',
          }
        : {
            boxShadow: 'inset 0 0 0 1px var(--co-line-strong)',
            color: 'var(--co-ink-4)',
          };

  return (
    <div
      aria-hidden="true"
      className="flex flex-none items-center justify-center rounded-full font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: FONT_SIZE[size],
        fontFamily: 'var(--co-font-mono)',
        ...style,
      }}
    >
      {initials}
    </div>
  );
}

export default CustomerAvatar;
